import { SideType } from 'scenes/MarketData/types'

import * as AT from './actionTypes'
import {
  LiveOrderType,
  MarginCallDetails,
  MarginOrderDetails,
  OrderMessageType,
  OrderRejectedType,
  Position
} from './types'

export type CancelActionType = {
  payload: { id: string | Array<string> }
  type: typeof AT.CANCEL
}

export type OrderActionType =
  | {
      payload: { order: LiveOrderType }
      type: typeof AT.ADD
    }
  | CancelActionType
  | { type: typeof AT.RESET }
  | {
      payload: {
        readonly orders: LiveOrderType[]
        readonly positions: Position[]
      }
      type: typeof AT.SET_SNAPSHOT
    }
  | { type: typeof AT.SET_SUBSCRIBED }
  | {
      payload: { readonly order: LiveOrderType }
      type: typeof AT.SET_UPDATE
    }
  | {
      payload: { readonly rejected: OrderRejectedType }
      type: typeof AT.SET_REJECTED
    }
  | { type: typeof AT.STOP }
  | FetchMarginOrderDetailsAction
  | MessageReceivedAction
  | SetMarginDetailsAction
  | SetActivePositionAction
  | UpdatePositionAction
  | FetchMarginCallDetailsAction
  | SetMarginCallDetailsAction

const add = (order: LiveOrderType): OrderActionType => ({
  payload: { order },
  type: AT.ADD
})

const cancel = (id: string | Array<string>): OrderActionType => ({
  payload: { id },
  type: AT.CANCEL
})

const reset = (): OrderActionType => ({
  type: AT.RESET
})

const setSnapshot = (orders: LiveOrderType[], positions: Position[]): OrderActionType => ({
  payload: { orders, positions },
  type: AT.SET_SNAPSHOT
})

const setSubscribed = (): OrderActionType => ({
  type: AT.SET_SUBSCRIBED
})

const setUpdate = (order: LiveOrderType): OrderActionType => ({
  payload: { order },
  type: AT.SET_UPDATE
})

const setRejected = (rejected: OrderRejectedType): OrderActionType => ({
  payload: { rejected },
  type: AT.SET_REJECTED
})

const stop = (): OrderActionType => ({
  type: AT.STOP
})

export type MarginOrderDetailsRequest = {
  amount: string
  collateralCurrency: string
  leverageRatio: number
  limitPrice: string
  side: SideType
  symbol: string
}
export type FetchMarginOrderDetailsAction = {
  payload: MarginOrderDetailsRequest
  type: typeof AT.FETCH_MARGIN_ORDER_DETAILS
}
const fetchMarginOrderDetails = (
  request: MarginOrderDetailsRequest
): FetchMarginOrderDetailsAction => ({
  payload: request,
  type: AT.FETCH_MARGIN_ORDER_DETAILS
})

export type CloseMarginPositionRequest = {
  ordType: 'limit' | 'market'
  orderQty: number
  positionId: number
  price?: number
}
export type CloseMarginPositionAction = {
  payload: CloseMarginPositionRequest
  type: typeof AT.CLOSE_MARGIN_POSITION
}
const closeMarginPosition = (request: CloseMarginPositionRequest): CloseMarginPositionAction => ({
  payload: request,
  type: AT.CLOSE_MARGIN_POSITION
})

export type AdjustMarginPositionRequest = {
  adjustmentAmount: number
  positionId: number
}
export type AdjustMarginPositionAction = {
  payload: AdjustMarginPositionRequest
  type: typeof AT.ADJUST_MARGIN_POSITION
}
const adjustMarginPosition = (
  request: AdjustMarginPositionRequest
): AdjustMarginPositionAction => ({
  payload: request,
  type: AT.ADJUST_MARGIN_POSITION
})

type MessageReceivedAction = {
  payload: OrderMessageType
  type: typeof AT.MESSAGE_RECEIVED
}
const messageReceived = (message: OrderMessageType): MessageReceivedAction => ({
  payload: message,
  type: AT.MESSAGE_RECEIVED
})

type SetMarginDetailsAction = {
  payload?: MarginOrderDetails
  type: typeof AT.SET_MARGIN_DETAILS
}
const setMarginDetails = (details?: MarginOrderDetails): SetMarginDetailsAction => ({
  payload: details,
  type: AT.SET_MARGIN_DETAILS
})

type SetMarginCallDetailsAction = {
  payload?: MarginCallDetails
  type: typeof AT.SET_MARGIN_CALL_DETAILS
}
const setMarginCallDetails = (details?: MarginCallDetails): SetMarginCallDetailsAction => ({
  payload: details,
  type: AT.SET_MARGIN_CALL_DETAILS
})

type SetActivePositionAction = {
  payload?: Position
  type: typeof AT.SET_ACTIVE_POSITION
}
const setActivePosition = (position?: Position): SetActivePositionAction => ({
  payload: position,
  type: AT.SET_ACTIVE_POSITION
})

type UpdatePositionAction = {
  payload: Position
  type: typeof AT.UPDATE_POSITION
}
const updatePosition = (position: Position): UpdatePositionAction => ({
  payload: position,
  type: AT.UPDATE_POSITION
})

type FetchMarginCallDetailsAction = {
  payload: { positionId: string }
  type: typeof AT.FETCH_MARGIN_CALL_DETAILS
}
const fetchMarginCallDetails = (positionId: string): FetchMarginCallDetailsAction => ({
  payload: { positionId },
  type: AT.FETCH_MARGIN_CALL_DETAILS
})

export default {
  add,
  adjustMarginPosition,
  cancel,
  closeMarginPosition,
  fetchMarginCallDetails,
  fetchMarginOrderDetails,
  messageReceived,
  reset,
  setActivePosition,
  setMarginCallDetails,
  setMarginDetails,
  setRejected,
  setSnapshot,
  setSubscribed,
  setUpdate,
  stop,
  updatePosition
}
